import type { FieldValues } from 'react-hook-form';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getCaptchaTheme } from '@/utils/theme/themeEngine';

import type { CaptchaResponse, CaptchaWidgetProps } from '../index';

const HCAPTCHA_PROVIDER = 'hcaptcha';
const MAX_RETRY_ATTEMPTS = 3;

const HCaptchaWidget = <T extends FieldValues = FieldValues>({
  config,
  name,
  rules,
  control,
  setError,
  clearErrors,
  onCaptchaResponse,
  theme,
  error,
}: CaptchaWidgetProps<T>) => {
  const retryCount = useRef(0);
  const [isClient, setIsClient] = useState(false);
  const { t } = useTranslation('common');

  useEffect(() => {
    // This effect runs only on the client side after the component mounts
    setIsClient(true);
  }, []);

  // Sync external server error into react-hook-form
  useEffect(() => {
    if (error && setError) {
      setError(name, { type: 'server', message: error });
    }
  }, [error, name, setError]);

  if (config.provider !== HCAPTCHA_PROVIDER) {
    return null;
  }

  const { siteKey } = config;
  if (!siteKey) {
    return null;
  }

  // Only render HCaptcha on the client side
  if (!isClient) {
    return null;
  }

  const handleVerify = (token: string, onChange: (value: string) => void) => {
    retryCount.current = 0;
    clearErrors?.(name);

    onChange(token);

    const response: CaptchaResponse = {
      provider: HCAPTCHA_PROVIDER,
      token: token,
    };
    onCaptchaResponse(response);
  };

  const handleError = (
    hcaptchaError: string,
    onChange: (value: string) => void,
  ) => {
    onChange('');
    onCaptchaResponse(null);

    if (retryCount.current < MAX_RETRY_ATTEMPTS) {
      retryCount.current += 1;
      return;
    }

    const translation = t('common:captcha.errorHCaptcha');
    setError?.(name, {
      type: 'widget',
      message: `${translation} ${hcaptchaError}`,
    });
  };

  const handleExpire = (onChange: (value: string) => void) => {
    onChange('');
    onCaptchaResponse(null);
    clearErrors?.(name);
    retryCount.current = 0;
  };

  const hcaptchaWidgetStyle = {
    transformOrigin: '0 0',
    overflow: 'hidden',
    display: 'inline-block',
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange }, fieldState }) => (
        <div>
          <div style={hcaptchaWidgetStyle}>
            <HCaptcha
              sitekey={siteKey}
              onVerify={(token) => handleVerify(token, onChange)}
              onExpire={() => handleExpire(onChange)}
              onError={(err) => handleError(err, onChange)}
              theme={getCaptchaTheme(theme)}
              size="normal"
            />
          </div>
          {fieldState.error?.message && (
            <StatusMessage className="u-margin-t-xs" mode="error">
              {fieldState.error.message}
            </StatusMessage>
          )}
        </div>
      )}
    />
  );
};

export default HCaptchaWidget;
